import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { authLogger } from './lib/auth/authLogger';
import { checkAccessValidation } from './lib/auth/accessValidation';
import { useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary/index';

const AuthNavigationGuard = React.lazy(() => import('./components/auth/AuthNavigationGuard'));
const Navbar = React.lazy(() => import('./components/navigation/Navbar'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-[200px] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
  </div>
);

// Wrap lazy imports in error boundaries
const LandingPage = React.lazy(() => import('./components/LandingPage'));
const About = React.lazy(() => import('./components/About'));
const AuthLayout = React.lazy(() => import('./components/navigation/AuthLayout'));
const LoginForm = React.lazy(() => import('./components/auth/LoginForm'));
const SignUpForm = React.lazy(() => import('./components/auth/SignUpForm'));
const ForgotPasswordForm = React.lazy(() => import('./components/auth/ForgotPasswordForm'));
const ResetPasswordForm = React.lazy(() => import('./components/auth/ResetPasswordForm'));
const ProfileForm = React.lazy(() => import('./components/auth/ProfileForm'));
const DecisionList = React.lazy(() => import('./components/decisions/DecisionList'));
const DecisionForm = React.lazy(() => import('./components/decisions/DecisionForm'));
const ProtectedRoute = React.lazy(() => import('./components/auth/ProtectedRoute'));
const DecisionTypeSelector = React.lazy(() => import('./components/DecisionTypeSelector'));
const DecisionDetails = React.lazy(() => import('./components/DecisionDetails'));
const ImportanceSelector = React.lazy(() => import('./components/ImportanceSelector'));
const ReversibilitySelector = React.lazy(() => import('./components/ReversibilitySelector'));
const GoalClarificationScreen = React.lazy(() => import('./components/GoalClarificationScreen'));
const Analysis = React.lazy(() => import('./components/Analysis'));

// Wrap component in error boundary and suspense
const WrappedComponent = ({ component: Component, ...props }: { component: React.ComponentType<any>; [key: string]: any }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>
      <Component {...props} />
    </Suspense>
  </ErrorBoundary>
);

// Wrap protected route component
const WrappedProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>
      <ProtectedRoute>{children}</ProtectedRoute>
    </Suspense>
  </ErrorBoundary>
);

export default function App() {
  const location = useLocation();
  const { authenticated, loading } = useAuth();
  const hasValidAccess = checkAccessValidation();

  // Only show navbar if not on landing page, auth routes, AND has valid access
  const isAuthRoute = ['/login', '/signup', '/forgot-password', '/reset-password'].includes(location.pathname);
  const showNavbar = location.pathname !== '/' && !isAuthRoute && (authenticated || hasValidAccess);

  // Show loading state while determining auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-white to-purple-100">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-purple-100">
          <AuthNavigationGuard />
          
          {showNavbar && (
            <Suspense fallback={<div className="h-16 bg-white shadow-sm" />}>
              <Navbar />
            </Suspense>
          )}
          
          <main className="container mx-auto px-4 py-8">
            <ErrorBoundary>
              <Routes>
              {/* Landing Page */}
              <Route path="/" element={<WrappedComponent component={LandingPage} />} />
              
              {/* Public Routes */}
              <Route path="/about" element={<WrappedComponent component={About} />} />
              
              {/* Auth Routes */}
              <Route element={<WrappedComponent component={AuthLayout} />}>
                <Route path="/login" element={<WrappedComponent component={LoginForm} />} />
                <Route path="/signup" element={<WrappedComponent component={SignUpForm} />} />
                <Route path="/forgot-password" element={<WrappedComponent component={ForgotPasswordForm} />} />
                <Route path="/reset-password" element={<WrappedComponent component={ResetPasswordForm} />} />
              </Route>

              {/* Protected Routes */}
              {hasValidAccess || authenticated ? (
                <>
                  <Route path="/decision">
                    <Route index element={<WrappedComponent component={DecisionTypeSelector} />} />
                    <Route path="details" element={<WrappedComponent component={DecisionDetails} />} />
                    <Route path="importance" element={<WrappedComponent component={ImportanceSelector} />} />
                    <Route path="reversibility" element={<WrappedComponent component={ReversibilitySelector} />} />
                    <Route path="goals" element={<WrappedComponent component={GoalClarificationScreen} />} />
                    <Route path="analysis" element={<WrappedComponent component={Analysis} />} />
                  </Route>

                  <Route path="/decisions" element={
                    <WrappedProtectedRoute>
                      <WrappedComponent component={DecisionList} />
                    </WrappedProtectedRoute>
                  } />
                  <Route path="/decisions/new" element={
                    <WrappedProtectedRoute>
                      <WrappedComponent component={DecisionForm} />
                    </WrappedProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <WrappedProtectedRoute>
                      <WrappedComponent component={ProfileForm} />
                    </WrappedProtectedRoute>
                  } />
                </>
              ) : (
                <Route path="*" element={<Navigate to="/" replace />} />
              )}

              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}